(function () {
  'use strict';

  class ProductListComponent extends HTMLElement {
    static get observedAttributes() {
      return ['product'];
    }

    constructor() {
      super();
      this._product = null;
    }

    connectedCallback() {
      if (!this._product && this.hasAttribute('product')) {
        this._product = this.parseProduct(this.getAttribute('product'));
      }

      this.render();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'product' || oldValue === newValue) return;

      this._product = this.parseProduct(newValue);
      this.render();
    }

    get product() {
      return this._product;
    }

    set product(value) {
      this._product = value;
      this.render();
    }

    parseProduct(value) {
      if (!value) return null;
      if (typeof value === 'object') return value;

      try {
        return JSON.parse(value);
      } catch (error) {
        return null;
      }
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    normalizeText(value) {
      if (value == null) return '';
      return String(value).trim();
    }

    getActiveVariant(product) {
      return product?.selected_variant || product?.selectedVariant || product?.variants?.[0] || null;
    }

    getVariantValues(product) {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const values = [];
      const seen = new Set();

      for (const variant of variants) {
        const rawValues = Array.isArray(variant?.selectedOptions) && variant.selectedOptions.length
          ? variant.selectedOptions.map(function (option) {
              return option?.value;
            })
          : Array.isArray(variant?.options) && variant.options.length
            ? variant.options
            : [variant?.option1, variant?.option2, variant?.option3, variant?.title];

        for (const rawValue of rawValues) {
          const value = this.normalizeText(rawValue);

          if (!value || value === 'Default Title' || seen.has(value)) continue;

          seen.add(value);
          values.push(value);
        }
      }

      if (values.length) return values;

      const optionValues = Array.isArray(product?.options)
        ? product.options
        : product?.options && typeof product.options === 'object'
          ? Object.values(product.options)
        : Array.isArray(product?.options_with_values)
          ? product.options_with_values.flatMap(function (option) {
              return Array.isArray(option?.values) ? option.values : [];
            })
          : [];

      for (const rawValue of optionValues) {
        const value = this.normalizeText(rawValue);

        if (!value || value === 'Default Title' || seen.has(value)) continue;

        seen.add(value);
        values.push(value);
      }

      return values;
    }

    getSelectedValues(product) {
      const variant = this.getActiveVariant(product);
      if (!variant) {
        return product?.options && typeof product.options === 'object'
          ? Object.values(product.options).map(this.normalizeText).filter(Boolean)
          : [];
      }

      const rawValues = Array.isArray(variant?.selectedOptions) && variant.selectedOptions.length
        ? variant.selectedOptions.map(function (option) {
            return option?.value;
          })
        : Array.isArray(variant?.options) && variant.options.length
          ? variant.options
          : [variant?.option1, variant?.option2, variant?.option3, variant?.title];

      return rawValues
        .map(this.normalizeText)
        .filter(function (value) {
          return value && value !== 'Default Title';
        });
    }

    renderValue(value, isActive) {
      const className = isActive
        ? 'rcc-search__product-list__value rcc-search__product-list__value--active'
        : 'rcc-search__product-list__value';

      return `<span class="${className}">${this.escapeHtml(value)}</span>`;
    }

    render() {
      const product = this.product;
      const values = product ? this.getVariantValues(product) : [];
      const selectedValues = new Set(product ? this.getSelectedValues(product) : []);

      if (!values.length) {
        this.innerHTML = '';
        return;
      }

      const markup = values
        .map((value, index) => {
          const suffix = index < values.length - 1 ? ', ' : '';
          return this.renderValue(value, selectedValues.has(value)) + suffix;
        })
        .join('');

      this.innerHTML = `<div class="rcc-search__product-list">${markup}</div>`;
    }
  }

  if (!customElements.get('rcc-product-list')) {
    customElements.define('rcc-product-list', ProductListComponent);
  }
})();
